import { FC } from 'react';
import { Field as FieldDefinition } from '@/types/core/Field';
import Stack from '../Stack/Stack';  
import Field from '../Field/Field';
import Input from '../Input/Input';
import Select from '../Select/Select';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
import Vector3, { Vector3Value } from '../Vector3/Vector3';
import Text from '../Text/Text';
import { getProperty, set } from '@/utils/object';
import { useCallback } from 'react';
import Button from '../Button/Button';
import { World } from '@/types/core/World';
import { Item } from '@/types/core/Item';
import { Material } from '@/types/core/Material';

import './DataForm.css';

interface DataFormProps {
  fields: FieldDefinition[];
  values: Record<string, any>;
  onChange: (changes: Record<string, any>) => void;
  world: World;
  selectItem: (id: string | null) => void;
}

const getFieldValue = (field: FieldDefinition, values: Record<string, any>) => {
  const value = getProperty(values, field.key);
  const useDefault = value === undefined || value === null;
  return useDefault ? field.default : value;
};

const DataForm: FC<DataFormProps> = ({ fields, values, onChange, world, selectItem }) => {
  const getFieldValueMemo = useCallback((field: FieldDefinition) => {
    return getFieldValue(field, values);
  }, [values]);

  const handleChange = useCallback((field: FieldDefinition, value: any) => {
    const newValues = { ...values };
    set(newValues, field.key, value);
    onChange(newValues);
  }, [values, onChange]);

  const renderField = useCallback((field: FieldDefinition) => {
    const value = getFieldValueMemo(field);

    switch (field.type) {
      case 'string':
        return (
          <Input 
            key={field.key} 
            type="text"
            value={value} 
            onChange={(value) => handleChange(field, value)} 
          />
        );
      case 'number':
        return (
          <Input 
            key={field.key} 
            type="number"
            value={value} 
            min={field.min}
            max={field.max}
            step={field.step}
            onChange={(value) => handleChange(field, value)} 
          />
        );
      case 'color':
        return (
          <Input 
            key={field.key} 
            type="color"
            value={value} 
            onChange={(value) => handleChange(field, value)} 
          />
        );
      case 'boolean':
        return (
          <Input 
            key={field.key} 
            type="checkbox"
            value={value} 
            onChange={(value) => handleChange(field, value)} 
          />
        );
      case 'vector3': {
        const vector: Vector3Value = value || { x: 0, y: 0, z: 0 };
        return (
          <Vector3
            key={field.key}
            value={vector}
            onChange={(value) => handleChange(field, value)}
            step={field.step ?? 0.1}
          />
        );
      }
      case 'select':
        return (
          <Select 
            key={field.key} 
            value={value} 
            onChange={(e) => handleChange(field, e.target.value)} 
            options={field.options} 
          />
        );
      case 'material':
        return (
          <Stack direction="vertical" gap="sm" className="material-field">
            <Select
              key={field.key}
              value={value || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              options={Object.values(world.items)
                .filter((item): item is Material => item.type === 'material')
                .map(material => ({
                  label: material.name,
                  value: material.id
                }))}
            />
            {value && (
              <Button 
                onClick={() => selectItem(value)}
                size="sm"
                variant="secondary"
              >
                Edit Material
              </Button>
            )}
          </Stack>
        );
      case 'button-group':
        return (
          <ButtonGroup
            key={field.key}
            value={value}
            onChange={(value) => handleChange(field, value)}
            options={field.options}
          />
        );
      default:
        return <Text key={field.key}>{value}</Text>;
    }
  }, [getFieldValueMemo, handleChange, world, selectItem]);

  return (
    <Stack className="data-form" direction="vertical" gap="md">
      {fields.map((field) => (
        <Field 
          key={field.key}
          field={field}
        >
          {renderField(field)}
        </Field>
      ))}
    </Stack>
  );
};

export default DataForm;